import isEqual from 'lodash/isEqual';

import {
  Box,
  Button,
  TextField,
  DialogTitle,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Alert,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import {
  addDoc,
  collection,
  getDocs,
  query,
  updateDoc,
  where,
  deleteDoc,
} from 'firebase/firestore';
import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useFacility } from '../../context/FacilityContext';
import { db } from '../../firebase/config';
import './Scheduler.css';
import { getUTCDate } from '../../utils/dayjsUs';
import { canAccessFacility } from '../../config/facilityAccess';

const schedulerSetData = [
  {
    sectionName: 'Day Nurse',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
  {
    sectionName: 'Night Nurse',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
  {
    sectionName: 'Day STNA',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
  {
    sectionName: 'Night STNA',
    sectionData: [
      {
        roomNumber: '',
        staffName: '',
      },
    ],
  },
];

function Scheduler() {
  const { facilityId } = useFacility();
  const [dateValue, setDateValue] = useState(getUTCDate());
  const [isLoading, setIsLoading] = useState(false);
  const { claims } = useAuth();
  const [showAlert, setShowAlert] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const [formData, setFormData] = useState(schedulerSetData);
  const [schedulerData, setSchedulerData] = useState(schedulerSetData);
  const [template, setTemplate] = useState({});
  const [open, setOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = React.useState('');
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(false);
  const [formChange, setFormChange] = useState(false);

  const handleSectionChange = (sectionIndex, rowIndex) => (event) => {
    const { name, value } = event.target;
    const updatedFormData = [...formData];
    updatedFormData[sectionIndex].sectionData[rowIndex][name] = value;
    setFormData(updatedFormData);
  };

  const addRow = (sectionIndex) => {
    setFormData((prev) => {
      const updatedSection = {
        ...prev[sectionIndex],
        sectionData: [
          ...prev[sectionIndex].sectionData,
          { roomNumber: '', staffName: '' },
        ],
      };

      const updatedFormData = prev.map((section, index) =>
        index === sectionIndex ? updatedSection : section,
      );

      return updatedFormData;
    });
  };

  const removeRow = (sectionIndex, rowIndex) => {
    setFormData((prev) => {
      const updatedSectionData = prev[sectionIndex].sectionData.filter(
        (_, i) => i !== rowIndex,
      );

      const updatedSection = {
        ...prev[sectionIndex],
        sectionData: updatedSectionData,
      };

      const updatedFormData = prev.map((section, index) =>
        index === sectionIndex ? updatedSection : section,
      );

      return updatedFormData;
    });
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    const newTemplate = {
      uid: Date.now(),
      templateName: newTemplateName,
      schedulerContent: undefined,
    };
    setTemplates([...templates, newTemplate]);
    setTemplate(newTemplate);
    addDoc(collection(db, 'scheduler'), {
      facilityId: facilityId,
      uid: newTemplate.uid,
      templateName: newTemplate.templateName,
      template: true,
    });
    setEditTemplate(false);
    setNewTemplateName('');
    setOpen(false);
    getSchedulerData();
  };

  const handleTemplateChange = (event) => {
    if (event.target.value === 99) {
      handleOpen();
    } else {
      setEditTemplate(false);
      setTemplate(
        templates.find((template) => template.uid === event.target.value),
      );
    }
  };

  const handleEditTemplate = () => {
    if (template.schedulerContent) {
      setFormData(template.schedulerContent);
    } else {
      setFormData(schedulerSetData);
    }
    setEditTemplate(true);
  };

  const handleApplyTemplate = () => {
    setEditTemplate(false);
    if (template.schedulerContent) {
      setFormData(template.schedulerContent);
    } else {
      setFormData(schedulerSetData);
    }
    addSchedulerData();
  };

  const handleDeleteTemplate = async () => {
    const querySnapshot = await getDocs(
      query(collection(db, 'scheduler'), where('uid', '==', template.uid)),
    );
    querySnapshot.forEach((doc) => {
      deleteDoc(doc.ref);
    });
    const newTemplates = templates.filter((t) => t.uid !== template.uid);
    setTemplates(newTemplates);
    if (
      template &&
      template.uid &&
      !newTemplates.find((t) => t.uid === template.uid)
    ) {
      setTemplate({});
    }
    setEditTemplate(false);
  };

  const getSchedulerData = async () => {
    if (editTemplate) return;
    setIsLoading(true);

    try {
      const q = query(
        collection(db, 'scheduler'),
        where('date', '==', dateValue.format('MM/DD/YYYY')),
        where('facilityId', '==', facilityId),
      );
      const querySnapshot = await getDocs(q);
      let schedulerData = [];

      querySnapshot.forEach((doc) => {
        schedulerData.push({ ...doc.data(), id: doc.id });
      });

      if (schedulerData.length > 0) {
        const copiedSchedulerData = JSON.parse(
          JSON.stringify(schedulerData[0].schedulerContent),
        );
        setFormData(copiedSchedulerData);
        setSchedulerData(schedulerData[0].schedulerContent);
      } else {
        setFormData(JSON.parse(JSON.stringify(schedulerSetData)));
        setSchedulerData(schedulerSetData);
      }
    } catch (error) {
      console.error('Error fetching scheduler data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTemplates = async () => {
    setIsLoading(true);
    try {
      const q = query(
        collection(db, 'scheduler'),
        where('facilityId', '==', facilityId),
        where('template', '==', true),
      );
      const querySnapshot = await getDocs(q);
      let templateData = [];
      querySnapshot.forEach((doc) => {
        templateData.push({ ...doc.data(), id: doc.id });
      });

      if (templateData.length > 0) {
        setTemplates(
          templateData.map((template) => ({
            uid: template.uid,
            templateName: template.templateName,
            schedulerContent: template.schedulerContent,
          })),
        );
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const addSchedulerData = async () => {
    if (!canEdit) return;
    if (editTemplate) {
      setTemplate({ ...template, schedulerContent: formData });
      const q = query(
        collection(db, 'scheduler'),
        where('facilityId', '==', facilityId),
        where('template', '==', true),
        where('uid', '==', template.uid),
      );
      getDocs(q).then((querySnapshot) => {
        if (!querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            updateDoc(doc.ref, { schedulerContent: formData });
          });
        }
      });
    } else {
      const schedulerdata = {
        schedulerContent: formData,
        facilityId: facilityId,
        date: dateValue.format('MM/DD/YYYY'),
      };
      const q = query(
        collection(db, 'scheduler'),
        where('date', '==', dateValue.format('MM/DD/YYYY')),
        where('facilityId', '==', facilityId),
      );
      getDocs(q).then((querySnapshot) => {
        if (!querySnapshot.empty) {
          querySnapshot.forEach((doc) => {
            updateDoc(doc.ref, schedulerdata);
          });
        } else {
          addDoc(collection(db, 'scheduler'), schedulerdata);
        }
      });

      setSchedulerData(JSON.parse(JSON.stringify(formData)));
      setFormChange(false);
    }
    setShowAlert(true);
    setTimeout(() => {
      setShowAlert(false);
    }, 2000);
  };

  useEffect(() => {
    if (editTemplate) return;
    setFormChange(!isEqual(formData, schedulerData));
  }, [formData]);

  useEffect(() => {
    const hasRequiredClaims = () => {
      if ('superadmin' in claims && claims.superadmin) return true;

      if ('admin' in claims && claims.admin) {
        return canAccessFacility(claims, facilityId);
      }
      if ('staff' in claims && claims.staff) {
        if (
          'facilities' in claims &&
          claims.staffPermissions.includes('scheduler')
        ) {
          return canAccessFacility(claims, facilityId);
        }
        return false;
      }
      return false;
    };

    setCanEdit(hasRequiredClaims());
  }, [claims]);

  useEffect(() => {
    getSchedulerData();
  }, [facilityId, dateValue, editTemplate]);

  useEffect(() => {
    getTemplates();
    setTemplate({ uid: '', templateName: '', schedulerContent: undefined });
  }, [facilityId]);

  return (
    <div className="schedulerContainer">
      <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
        {showAlert && (
          <Alert
            severity="success"
            onClose={() => {
              setShowAlert(false);
            }}
          >
            Successfully saved!
          </Alert>
        )}
        <div className="schedulerHeader">
          <div className="schedulerHeaderCalendarContainer">
            <DateCalendar
              disabled={editTemplate || formChange}
              className="schedulerHeaderCalendar"
              views={['day']}
              value={dateValue}
              onChange={(newValue) => setDateValue(newValue)}
              timezone="UTC"
            />
          </div>
          <div className="schedulerHeaderTemplateControl">
            <Button
              type="button"
              className="screenshotButton"
              variant="outlined"
              startIcon={<CameraAltIcon />}
              onClick={() => {
                document.body.classList.add('screenshotMode');
                const keydownHandler = (event) => {
                  if (event.key === 'Escape' || event.key === 'Delete') {
                    document.body.classList.remove('screenshotMode');
                    document.removeEventListener('keydown', keydownHandler);
                  }
                };
                document.addEventListener('keydown', keydownHandler);

                setTimeout(() => {
                  window.print();
                }, 600);

                setTimeout(() => {
                  if (document.body.classList.contains('screenshotMode')) {
                    alert(
                      'Press escape(ESC) or delete (DEL) on your keyboard to exit the screenshot mode',
                    );
                  }
                }, 5000);
              }}
            >
              Print
            </Button>

            <hr className="templateControlDivider" />

            <FormControl fullWidth>
              <InputLabel>Select a template</InputLabel>
              <Select
                value={template.uid || ''}
                label="Select a template"
                onChange={handleTemplateChange}
              >
                {templates.map((template) => (
                  <MenuItem key={template.uid} value={template.uid}>
                    {template.templateName}
                  </MenuItem>
                ))}
                <MenuItem value={99}>Create a new template...</MenuItem>
              </Select>
            </FormControl>
            <Dialog open={open} onClose={handleClose}>
              <DialogTitle>Create a new template</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Please enter the name of the new template.
                </DialogContentText>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Template Name"
                  type="text"
                  fullWidth
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave}>Save</Button>
              </DialogActions>
            </Dialog>
            <div className="templateControlButtons">
              {!editTemplate && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleEditTemplate}
                    disabled={!template.uid}
                  >
                    Edit Template
                  </Button>
                  <Button
                    className="applyTemplateButton"
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={handleApplyTemplate}
                    disabled={!template.uid}
                  >
                    Apply Template
                  </Button>
                </>
              )}

              {editTemplate && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDeleteTemplate}
                    disabled={!template.uid}
                  >
                    Delete Template
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      addSchedulerData();
                      setEditTemplate(false);
                      getSchedulerData();
                    }}
                    disabled={!template.uid}
                  >
                    Save Template
                  </Button>
                </>
              )}
            </div>
            {formChange && (
              <div className="templateControlButtons">
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={addSchedulerData}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    disableElevation
                    onClick={() => {
                      setEditTemplate(false);
                      setFormData(JSON.parse(JSON.stringify(schedulerData)));
                    }}
                  >
                    Rest
                  </Button>
                </>
              </div>
            )}
          </div>
        </div>

        <hr />

        <div className="schedulerPrintDate">
          Schedule Date: {dateValue.format('MMMM D, YYYY')}
        </div>

        <Box className="schedulerTable">
          {formData.map((section, sectionIndex) => (
            <div className="schedulerTableSegment" key={sectionIndex}>
              {canEdit ? (
                <input
                  className="segmentHeader"
                  type="text"
                  name="sectionName"
                  value={section.sectionName}
                  onChange={(e) => {
                    const updatedFormData = [...formData];
                    updatedFormData[sectionIndex].sectionName = e.target.value;
                    setFormData(updatedFormData);
                  }}
                />
              ) : (
                <div className="segmentHeader">
                  {section.sectionName.replace(/([A-Z])/g, ' $1').toUpperCase()}
                </div>
              )}

              {section.sectionData.map((row, rowIndex) => (
                <div className="segmentRow" key={rowIndex}>
                  <div className="cell">
                    <input
                      className="rowLocation"
                      readOnly={!canEdit}
                      type="text"
                      name="roomNumber"
                      value={row.roomNumber}
                      onChange={handleSectionChange(sectionIndex, rowIndex)}
                    />
                    <div className="printCell" aria-hidden="true">
                      {row.roomNumber}
                    </div>
                  </div>

                  <div className="cell">
                    <input
                      className="rowStaff"
                      readOnly={!canEdit}
                      type="text"
                      name="staffName"
                      value={row.staffName}
                      onChange={handleSectionChange(sectionIndex, rowIndex)}
                    />
                    <div className="printCell" aria-hidden="true">
                      {row.staffName}
                    </div>
                  </div>

                  {section.sectionData.length > 1 && canEdit && (
                    <Button
                      className="segmentRemoveButton"
                      variant="outlined"
                      color="error"
                      onClick={() => removeRow(sectionIndex, rowIndex)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}

              {canEdit && (
                <div className="segmentAddButtonContainer">
                  <Button
                    variant="outlined"
                    className="segmentAddButton"
                    onClick={() => addRow(sectionIndex)}
                  >
                    Add Row
                  </Button>
                </div>
              )}
            </div>
          ))}
        </Box>
      </DashboardLayout>
    </div>
  );
}

Scheduler.propTypes = {};

export default Scheduler;
